import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import {
  db,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  orderBy,
  doc,
  setDoc,
  serverTimestamp,
  getDocs,
} from '../firebase';
import { directConversationId } from '../lib/chat';

/** MediaRecorder / file inputs often omit type or use octet-stream — Storage rules require image|video|audio. */
function normalizeChatMime(file, kindHint) {
  const raw = (file.type || '').trim();
  if (raw && raw !== 'application/octet-stream' && !raw.startsWith('text/')) {
    return raw;
  }
  const name = (file.name || '').toLowerCase();
  if (kindHint === 'audio') return 'audio/webm';
  if (kindHint === 'video') return 'video/webm';
  if (kindHint === 'image') return 'image/jpeg';
  if (name.endsWith('.webm')) return kindHint === 'audio' ? 'audio/webm' : 'video/webm';
  if (name.endsWith('.mp4') || name.endsWith('.m4v')) return 'video/mp4';
  if (name.endsWith('.mov')) return 'video/quicktime';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.mp3')) return 'audio/mpeg';
  if (name.endsWith('.wav')) return 'audio/wav';
  if (name.endsWith('.ogg')) return 'audio/ogg';
  return 'video/webm';
}

function messageTypeFromMime(mime) {
  if (!mime) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'image';
}

function MessageContent({ msg, uid }) {
  const mine = msg.sender === uid;
  const type = msg.type || (msg.mediaUrl ? messageTypeFromMime(msg.mimeType) : 'text');

  const bubbleClass = `haydi-chat__bubble${mine ? ' haydi-chat__bubble--mine' : ' haydi-chat__bubble--theirs'}`;

  if (type === 'image' && msg.mediaUrl) {
    return (
      <span className={bubbleClass}>
        <img src={msg.mediaUrl} alt="" className="haydi-chat__media" />
        {msg.text ? <div style={{ marginTop: 6 }}>{msg.text}</div> : null}
      </span>
    );
  }
  if (type === 'video' && msg.mediaUrl) {
    return (
      <span className={bubbleClass}>
        <video src={msg.mediaUrl} className="haydi-chat__media" controls playsInline />
        {msg.text ? <div style={{ marginTop: 6 }}>{msg.text}</div> : null}
      </span>
    );
  }
  if (type === 'audio' && msg.mediaUrl) {
    return (
      <span className={bubbleClass}>
        <audio src={msg.mediaUrl} controls style={{ minWidth: 200, maxWidth: '100%' }} />
        {msg.text ? <div style={{ marginTop: 6 }}>{msg.text}</div> : null}
      </span>
    );
  }
  return <span className={bubbleClass}>{msg.text || ''}</span>;
}

function ChatTab({ user }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [dmTarget, setDmTarget] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(null);

  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const uid = user?.uid;

  useEffect(() => {
    (async () => {
      const us = await getDocs(collection(db, 'users'));
      setUsers(us.docs.map((d) => ({ _id: d.id, ...d.data() })));
    })();
  }, []);

  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', uid),
    );
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
      setConversations(rows);
    });
  }, [uid]);

  useEffect(() => {
    const cid = searchParams.get('c');
    if (!cid || !conversations.length) return;
    const found = conversations.find((c) => c._id === cid);
    if (found) {
      setActiveConversation(found);
      const next = new URLSearchParams(searchParams);
      next.delete('c');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, conversations, setSearchParams]);

  useEffect(() => {
    if (!activeConversation) {
      setMessages([]);
      return;
    }
    const q = query(
      collection(db, 'conversations', activeConversation._id, 'messages'),
      orderBy('createdAt', 'asc'),
    );
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    });
  }, [activeConversation]);

  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
    }
  }, []);

  const touchConversation = useCallback(async (convId) => {
    await setDoc(
      doc(db, 'conversations', convId),
      { updatedAt: serverTimestamp() },
      { merge: true },
    );
  }, []);

  const sendPayload = useCallback(
    async (payload) => {
      if (!activeConversation || !uid) return;
      await addDoc(collection(db, 'conversations', activeConversation._id, 'messages'), {
        sender: uid,
        ...payload,
        createdAt: serverTimestamp(),
      });
      await touchConversation(activeConversation._id);
    },
    [activeConversation, uid, touchConversation],
  );

  const uploadAndSend = useCallback(
    async (file, kindHint) => {
      if (!file || !activeConversation || !uid) return;
      setUploading(true);
      try {
        const safe = (file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `chat/${activeConversation._id}/${uid}/${Date.now()}_${safe}`;
        const storageRef = ref(storage, path);
        const mime = normalizeChatMime(file, kindHint);
        await uploadBytes(storageRef, file, { contentType: mime });
        const url = await getDownloadURL(storageRef);
        const type = messageTypeFromMime(mime);
        await sendPayload({
          type,
          text: '',
          mediaUrl: url,
          mimeType: mime,
        });
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert(e.message || t('chatMediaError'));
      } finally {
        setUploading(false);
      }
    },
    [activeConversation, uid, sendPayload, t],
  );

  const sendBlobMedia = useCallback(
    async (blob, ext, mime, kindHint) => {
      if (!blob?.size || !activeConversation || !uid) return;
      const name = `rec_${Date.now()}.${ext}`;
      const file = new File([blob], name, { type: mime || (kindHint === 'audio' ? 'audio/webm' : 'video/webm') });
      await uploadAndSend(file, kindHint);
    },
    [activeConversation, uid, uploadAndSend],
  );

  const handleSendMessage = async () => {
    if (!message.trim() || !activeConversation || !uid) return;
    await sendPayload({ type: 'text', text: message.trim(), mediaUrl: null, mimeType: null });
    setMessage('');
  };

  const startDm = async () => {
    if (!dmTarget || !uid) return;
    const convId = directConversationId(uid, dmTarget);
    await setDoc(
      doc(db, 'conversations', convId),
      {
        participants: [String(uid), String(dmTarget)].sort(),
        isGroup: false,
        name: '',
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    const conv = {
      _id: convId,
      participants: [String(uid), String(dmTarget)].sort(),
      isGroup: false,
    };
    setConversations((prev) => {
      const exists = prev.find((c) => c._id === convId);
      if (exists) return prev;
      return [conv, ...prev];
    });
    setActiveConversation(conv);
    setDmTarget('');
  };

  const createGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0 || !uid) return;
    const participants = [...new Set([String(uid), ...groupMembers.map(String)])];
    const docRef = await addDoc(collection(db, 'conversations'), {
      participants,
      isGroup: true,
      name: groupName.trim(),
      updatedAt: serverTimestamp(),
    });
    const conv = {
      _id: docRef.id,
      participants,
      isGroup: true,
      name: groupName.trim(),
    };
    setActiveConversation(conv);
    setGroupName('');
    setGroupMembers([]);
  };

  const otherName = (conv) => {
    if (!uid) return '';
    if (conv.isGroup) return conv.name || t('groupChat');
    const other = conv.participants.find((id) => String(id) !== String(uid));
    return users.find((u) => u._id === other)?.name || '…';
  };

  const threadInitial = (conv) => {
    const n = otherName(conv);
    return (n && n.trim().charAt(0).toUpperCase()) || '•';
  };

  const stopRecorder = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    setRecording(null);
  };

  const startAudioRecording = async () => {
    if (!activeConversation || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        stopRecorder();
        await sendBlobMedia(blob, 'webm', blob.type || 'audio/webm', 'audio');
      };
      rec.start();
      recorderRef.current = rec;
      setRecording('audio');
    } catch (e) {
      window.alert(e.message || t('chatMediaError'));
      stopRecorder();
    }
  };

  const startVideoRecording = async () => {
    if (!activeConversation || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'video/webm' });
        stopRecorder();
        await sendBlobMedia(blob, 'webm', blob.type || 'video/webm', 'video');
      };
      rec.start();
      recorderRef.current = rec;
      setRecording('video');
    } catch (e) {
      window.alert(e.message || t('chatMediaError'));
      stopRecorder();
    }
  };

  const onImageChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) uploadAndSend(f, 'image');
  };
  const onVideoFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) uploadAndSend(f, 'video');
  };

  const composerDisabled = !activeConversation || uploading || !!recording;

  return (
    <div className="haydi-chat haydi-chat--modern">
      <div className="haydi-pro-hero">
        <h1 className="haydi-pro-hero__title">{t('chat')}</h1>
        <p className="haydi-pro-hero__lead">{t('chatSubtitle')}</p>
      </div>

      <details className="haydi-chat__start-panel">
        <summary>{t('chatNewChat')} · {t('chatNewGroup')}</summary>
        <div className="haydi-chat__start-body">
          <div className="haydi-form-row" style={{ marginBottom: '1rem' }}>
            <span className="haydi-muted" style={{ alignSelf: 'center', fontWeight: 600 }}>{t('chatStartDm')}</span>
            <select
              className="haydi-select haydi-input--inline"
              value={dmTarget}
              onChange={(e) => setDmTarget(e.target.value)}
              style={{ minWidth: 160, flex: 1 }}
            >
              <option value="">{t('selectUserChat')}</option>
              {users.filter((u) => u._id !== uid).map((u) => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
            <button type="button" className="haydi-btn haydi-btn--primary" onClick={startDm} disabled={!dmTarget}>
              {t('openChat')}
            </button>
          </div>
          <div className="haydi-form-row" style={{ marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <input
              className="haydi-input haydi-input--inline"
              style={{ minWidth: 160, flex: 1 }}
              placeholder={t('chatGroupName')}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <button
              type="button"
              className="haydi-btn haydi-btn--primary"
              onClick={createGroup}
              disabled={!groupName.trim() || groupMembers.length === 0}
            >
              {t('chatCreateGroup')}
            </button>
          </div>
          <label className="haydi-muted" style={{ fontSize: '0.8125rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>
            {t('chatGroupMembers')}
          </label>
          <select
            multiple
            className="haydi-select haydi-input"
            size={Math.min(5, Math.max(3, users.length))}
            value={groupMembers}
            onChange={(e) => setGroupMembers([...e.target.selectedOptions].map((o) => o.value))}
          >
            {users.filter((u) => u._id !== uid).map((u) => (
              <option key={u._id} value={u._id}>{u.name}</option>
            ))}
          </select>
        </div>
      </details>

      <input ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onImageChange} />
      <input ref={vidInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={onVideoFileChange} />

      <div className="haydi-chat__shell">
        <aside className="haydi-chat__sidebar">
          <div className="haydi-chat__sidebar-title">{t('chatThreadList')}</div>
          {conversations.map((conv) => (
            <div
              key={conv._id}
              className={`haydi-chat__thread-row${activeConversation && activeConversation._id === conv._id ? ' haydi-chat__thread-row--active' : ''}`}
              onClick={() => setActiveConversation(conv)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setActiveConversation(conv);
              }}
            >
              <div className="haydi-chat__thread-avatar" aria-hidden>{threadInitial(conv)}</div>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{otherName(conv)}</span>
            </div>
          ))}
        </aside>
        <div className="haydi-chat__main">
          <div className="haydi-chat__messages">
            {messages.map((msg) => (
              <div
                key={msg._id}
                style={{
                  textAlign: msg.sender === uid ? 'right' : 'left',
                  marginBottom: '0.65rem',
                }}
              >
                <MessageContent msg={msg} uid={uid} />
              </div>
            ))}
          </div>

          {recording && (
            <div className="haydi-chat__record-hint" style={{ padding: '0.35rem 1rem' }}>
              {recording === 'audio' ? t('chatRecordingAudio') : t('chatRecordingVideo')}
              {' — '}
              <button type="button" className="haydi-btn haydi-btn--secondary haydi-btn--sm" onClick={() => recorderRef.current?.stop()}>
                {t('chatStopRecording')}
              </button>
            </div>
          )}

          <div className="haydi-chat__composer haydi-chat__composer--modern">
            <div className="haydi-chat__composer-pill">
              <div className="haydi-chat__composer-tools" style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  type="button"
                  className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                  disabled={composerDisabled}
                  onClick={() => imgInputRef.current?.click()}
                  title={t('chatAttachImage')}
                >
                  {t('chatAttachImage')}
                </button>
                <button
                  type="button"
                  className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                  disabled={composerDisabled}
                  onClick={() => vidInputRef.current?.click()}
                  title={t('chatAttachVideo')}
                >
                  {t('chatAttachVideo')}
                </button>
                <button
                  type="button"
                  className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                  disabled={composerDisabled || !!recording}
                  onClick={startAudioRecording}
                >
                  {t('chatRecordAudio')}
                </button>
                <button
                  type="button"
                  className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                  disabled={composerDisabled || !!recording}
                  onClick={startVideoRecording}
                >
                  {t('chatRecordVideo')}
                </button>
                <button
                  type="button"
                  className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                  disabled={composerDisabled}
                  onClick={() => setShowEmoji((v) => !v)}
                >
                  {t('chatEmoji')}
                </button>
                {showEmoji && (
                  <div className="haydi-chat__emoji-pop">
                    <EmojiPicker
                      theme={Theme.DARK}
                      width={300}
                      height={380}
                      onEmojiClick={(d) => {
                        setMessage((m) => m + d.emoji);
                        setShowEmoji(false);
                      }}
                    />
                  </div>
                )}
              </div>
              <input
                type="text"
                className="haydi-input"
                placeholder={uploading ? t('chatSending') : t('chatPlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={composerDisabled}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSendMessage();
                }}
                style={{ flex: 1, minWidth: 0 }}
              />
            </div>
            <button
              type="button"
              className="haydi-btn haydi-btn--primary"
              onClick={handleSendMessage}
              disabled={composerDisabled || !message.trim()}
              style={{ flexShrink: 0 }}
            >
              {t('send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatTab;
